(ns hai.contacts.create
  "Standalone contact creation in the editor ([[hai.contacts.editor]]): the
   contacts page opens it on a fresh contact, the Progress timeline's + action
   on one prefilled from an auto-discovered person or org — Save creates it
   and closes the editor. Mount [[overlay]] once at the app root. Mirrors
   [[hai.notes.create]]."
  (:require
   [hai.contacts.data :as data]
   [hai.contacts.editor :as editor]
   [hai.contacts.form :as form]
   [hai.contacts.mutations :as mutations]
   [hai.entity.draft :as draft]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.web-app.routing :as routing]
   [hai.web-app.viewport :as viewport]))

(def draft-id
  "The draft behind the \"Creating contact\" editor."
  ::draft)

(defsub ::open? [db]
  (get db ::open? false))

;; `initial` seeds the draft for a host that already knows something — the
;; timeline hands over what it auto-discovered; the contacts page nothing.
(defevent ::open [db initial]
  (-> db
      (assoc ::open? true ::initial initial)
      (draft/seed draft-id (form/from-contact initial))))

;; The draft goes with the editor: reopening always starts fresh.
(defevent ::close [db _]
  (-> db
      (dissoc ::open? ::initial)
      (draft/discard draft-id)))

;; A new contact has no saved state to restore, so Cancel puts the draft back
;; to what the editor opened on.
(defevent ::reset [db _]
  (draft/seed db draft-id (form/from-contact (::initial db))))

;; Refresh whatever lists the contact: the contacts page when mounted, and the
;; timeline's contact directory, which flags its filter rows as contacts.
(defn- refresh-fx [db]
  (cond-> [[:dispatch [:timeline/load-contact-directory]]]
    (get-in db [:collections data/table-id])
    (conj [:dispatch [:table/resolve data/table-id]])))

;; The contact as filed — the save's `'?result` — carries the uuid the server
;; minted (or deduplicated onto), so the toast can link to it.
(defevent-fx ::added-toast
  [_ contact]
  {:fx [[:dispatch [:toast/success
                    (cond-> {:text "Contact added"}
                      (:contact/uuid contact)
                      (assoc :action
                             {:label  "View contact"
                              :href   (routing/href :contacts/detail {:contact/uuid (:contact/uuid contact)})
                              :target routing/link-target}))]]]})

;; The editor stays up, Save spinning, until the contact is filed — then
;; closes; an error toasts and leaves the draft where it is.
(defevent-fx ::save
  [{:keys [db]} staged]
  (when-not (request/in-flight? db ::save)
    {:db (request/mark-in-flight db ::save)
     :resolve (assoc (mutations/save-resolve nil staged [:contact/uuid])
                     :request-id ::save
                     :on-success-fx (conj (refresh-fx db)
                                          [:dispatch [::close]]
                                          [:dispatch [::added-toast '?result]])
                     :on-error-fx [[:dispatch [:toast/error {:text "Could not save contact. Try again."}]]])}))

(defn overlay
  "The \"Creating contact\" editor — the modal on desktop, the full-screen
   editor on mobile. Mount once at the app root; opened via [[::open]]."
  []
  (let [open?   @(<s [::open?])
        mobile? @(<s [::viewport/mobile?])
        saving? (= :start @(<s [:fetch/state ::save]))
        editor-props {:title      "Creating contact"
                      ;; No contact yet: the composer is on a draft of its own.
                      :contact    nil
                      :draft-id   draft-id
                      :show-hint? (not mobile?)
                      :saving?    saving?
                      :on-save    (fn [staged] (>e [::save staged]))
                      :on-cancel  #(>e [::reset])
                      :on-close   #(>e [::close])}]
    (if mobile?
      (when open?
        [editor/screen editor-props])
      [editor/dialog
       {:open?    open?
        :on-close #(>e [::close])
        :title    "Creating contact"}
       (when open?
         [editor/modal-content editor-props])])))
